<template>
  <div :class="[labelPosition === 'side' ? 'flex no-wrap' : '']">
    <div
      :class="[labelPosition === 'side' ? 'q-mr-lg q-mt-xs' : '']"
      :style="{
        paddingTop: `${labelOffset[0]}px`,
        paddingRight: `${labelOffset[1]}px`,
      }"
    >
      <div
        v-if="label || cta?.label"
        :class="computedLabelClasses"
        class="flex justify-between items-center"
      >
        <p v-if="label" class="q-mb-none">
          <span>{{ label }}</span>
          <span v-if="isRequired" class="text-negative"> * </span>
        </p>
        <slot name="actionComponent">
          <PBtn v-if="cta?.label" v-bind="cta" />
        </slot>
      </div>
      <p v-if="help" :class="helpClasses">
        <span>{{ help }}</span>
      </p>
    </div>
    <q-select
      ref="component"
      v-bind="$attrs"
      :class="[labelPosition === 'side' ? 'col' : '']"
      :clearable="$attrs['clearable'] ?? (!isRequired && noMultipleValues)"
      :disable="$attrs['disable']"
      :emit-value="$attrs['emit-value'] ?? true"
      :map-options="$attrs['map-options'] ?? true"
      :hide-selected="noMultipleValues"
      :input-style="inputStyle"
      :placeholder="noMultipleValues ? $attrs.placeholder : ''"
      :popup-content-class="popupContentClasses"
      :popup-content-style="popupContentStyle"
      :rules="validationRules"
      bg-color="neutral-10"
      label-color="neutral-80"
      dropdown-icon="sym_r_expand_more"
      options-selected-class="bg-primary-40 text-neutral"
      fill-input
      no-error-icon
      outlined
      use-input
      @popup-show="onPopupShow"
      @popup-hide="removeScrollEventListener"
      @update:model-value="onUpdateModelValue"
    >
      <template
        v-for="(slot, index) of Object.keys($slots)"
        :key="index"
        #[slot]="slotProps"
      >
        <slot v-if="slotProps" v-bind="slotProps" :name="slot"></slot>
        <slot v-else :name="slot"></slot>
      </template>

      <template #no-option>
        <q-item class="text-neutral text-sm" dense>
          <q-item-section> {{ noOptionsText }} </q-item-section>
        </q-item>
      </template>

      <template #option="{ itemProps, opt, selected }">
        <q-item
          v-bind="itemProps"
          :class="{ 'text-neutral': !selected }"
          class="q-py-xs"
          dense
        >
          <q-item-section>
            <q-item-label :class="opt?.labelClass" class="text-sm">
              {{ opt[$attrs["option-label"] || "label"] ?? opt }}
              {{ opt[$attrs["label-suffix"]] || "" }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <template v-if="optionsLoading" #after-options>
        <q-item class="flex items-center justify-center text-primary text-xxl">
          <q-spinner-dots />
        </q-item>
      </template>
    </q-select>
  </div>
</template>

<script setup>
import { isEmpty } from "assets/scripts/utilities/helpers";
import validationsToRules from "assets/scripts/utilities/validations";

const props = defineProps({
  cta: {
    type: Object,
    default: () => {
      return {};
    },
  },
  contentWidth: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  labelClasses: {
    type: String,
    default: "text-sm text-neutral",
  },
  labelPosition: {
    type: String,
    default: "top",
  },
  labelOffset: {
    type: Array,
    default: () => [0, 0],
  },
  noOptionsText: {
    type: String,
    default: "No results",
  },
  help: {
    type: String,
    default: "",
  },
  helpClasses: {
    type: String,
    default: "text-xs text-neutral-70 q-mb-xs",
  },
  optionsLoading: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => {
      return [];
    },
  },
  validations: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const computedLabelClasses = computed(() => {
  if (props.help) {
    return `${props.labelClasses} q-mb-none`;
  }

  if (props.cta?.label) {
    return `${props.labelClasses} q-mb-xxs`;
  }

  return `${props.labelClasses} q-mb-xs`;
});

const component = ref(null);

defineExpose({
  component,
});

const attrs = useAttrs();

const onUpdateModelValue = () => {
  if (attrs.multiple) {
    component.value.updateInputValue("");
  }
};

const isRequired = computed(() => {
  return props.required || props.validations?.required;
});

const validationRules = computed(() => {
  if (props.required || !isEmpty(props.validations) || props.rules.length) {
    return [
      ...validationsToRules({
        label: props.validations?.label || props.label,
        required: unref(isRequired),
        type: props.validations?.type,
        ...props.validations,
      }),
      ...props.rules,
    ];
  }

  return [];
});

const noMultipleValues = computed(() => {
  return attrs.multiple === undefined || !attrs.modelValue?.length;
});

const popupContentClasses = computed(() => {
  return attrs.dense !== undefined ? "dense p-select-input" : "p-select-input";
});

const inputStyle = ref({
  width: "auto",
  maxWidth: "auto",
});

const updateInputWidth = (value) => {
  if (props.contentWidth) {
    let modelValue = attrs["emit-value"]
      ? value
      : value?.[attrs["option-label"]];

    if (modelValue) {
      inputStyle.value.width = `calc(${modelValue?.length}ch - ${modelValue?.length}px)`;

      inputStyle.value.maxWidth = "250px";
    } else {
      inputStyle.value.width = "auto";

      inputStyle.value.maxWidth = "auto";
    }
  }
};

onUpdated(() => {
  nextTick(() => updateInputWidth(attrs.modelValue));
});

let popupContentStyle = ref({
  width: "150px",
});

const onFormBodyScroll = () => {
  unref(component)?.updateMenuPosition();
};

const formBody = shallowRef(null);

const addScrollEventListener = () => {
  formBody.value = unref(component).$el?.form?.querySelector(".form-body");

  unref(formBody)?.addEventListener("scroll", onFormBodyScroll);
};

const removeScrollEventListener = () => {
  unref(formBody)?.removeEventListener("scroll", onFormBodyScroll);
};

const onPopupShow = () => {
  addScrollEventListener();

  unref(popupContentStyle).width = `calc(${
    unref(component).$el.offsetWidth
  }px - 37px) !important`;
};
</script>

<style lang="scss">
.p-select-input {
  .q-virtual-scroll {
    &__content {
      .q-item {
        .q-focus-helper {
          display: none;
        }

        &.disabled {
          color: $neutral-70;
          background-color: $neutral-30;

          .text-caption {
            color: $neutral-60;

            * {
              color: $neutral-60;
            }
          }
        }

        &:not(&--active, .disabled) {
          &:hover,
          &.q-manual-focusable--focused {
            background-color: $primary-30;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.q-field {
  :deep(.q-field__inner) {
    .q-field {
      &__control {
        align-items: center;
        min-height: 38px;
        overflow: hidden;

        &::before {
          border-color: $neutral-50;
        }

        &:not(.text-negative):hover::after {
          border-color: $primary-80;
        }

        &:not(.text-negative):focus-within::after {
          border-color: $primary;
        }
      }

      &__marginal {
        height: 38px;
      }

      &__native {
        min-height: 38px;

        &::placeholder {
          color: $neutral-50;
        }

        .q-chip {
          background: $neutral-40;
          color: $neutral;
        }
      }

      &__input {
        &::placeholder {
          color: $neutral-60;
        }
      }
    }
  }

  &.prepend-bg :deep(.q-field__inner) {
    .q-field {
      &__control {
        padding: 0 12px 0 0;
      }

      &__prepend {
        background: $neutral-40;

        justify-content: center;

        min-width: 38px;

        padding: 0 6px;
      }

      &__native {
        padding-left: 12px;
      }
    }
  }

  &--dense {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          min-height: 30px;
        }

        &__marginal {
          height: 30px;
        }

        &__native {
          min-height: 30px;
        }
      }
    }
  }

  &--disabled {
    :deep(.q-field__inner) {
      .q-field {
        &__control {
          background-color: $neutral-30;
        }
      }
    }
  }
}
</style>
